import { useEffect, useMemo, useState } from "preact/hooks";
import { browserLanguages, loadLocale, resolveLocale, saveLocale, type Locale } from "./locale.ts";

export { intlLocale, locales, type Locale } from "./locale.ts";

/*
 * Traductions de l'interface, sans dépendance : chaque composant déclare ses textes à côté de lui
 * avec defineMessages(fr, en) et les lit avec useT(messages). Les valeurs sont des chaînes avec
 * des emplacements {nom}, ou des fonctions pour les accords (pluriels, etc.).
 */

type Message = string | ((vars: never) => string);
type MessageRecord = Record<string, Message>;
type Vars = Record<string, string | number>;

/** Traduction anglaise : exactement les clés françaises, avec les mêmes variables. */
export type Translation<M extends MessageRecord> = {
  [K in keyof M]: M[K] extends (vars: infer A) => string ? (vars: A) => string : string;
};

export interface Messages<M extends MessageRecord> {
  fr: M;
  en: Translation<M>;
}

export function defineMessages<M extends MessageRecord>(fr: M, en: NoInfer<Translation<M>>): Messages<M> {
  return { fr, en };
}

type Args<V> = V extends (vars: infer A) => string ? [vars: A] : [vars?: Vars];

export type Translate<M extends MessageRecord> = <K extends keyof M & string>(key: K, ...args: Args<M[K]>) => string;

const interpolate = (text: string, vars: Vars | undefined) =>
  vars ? text.replace(/\{(\w+)\}/g, (match, name: string) => (name in vars ? String(vars[name]) : match)) : text;

/** Traducteur hors composant (fonctions utilitaires, tests). */
export function translator<M extends MessageRecord>(messages: Messages<M>, locale: Locale): Translate<M> {
  const table = (locale === "en" ? messages.en : messages.fr) as MessageRecord;
  return (key, ...args) => {
    const message = table[key] ?? messages.fr[key]!;
    const vars = args[0] as Vars | undefined;
    return typeof message === "function" ? (message as (vars: unknown) => string)(vars) : interpolate(message, vars);
  };
}

// Langue active, partagée par tous les composants (plusieurs racines Preact possibles).
let current: Locale = resolveLocale(loadLocale(), browserLanguages());
const listeners = new Set<(locale: Locale) => void>();
if (typeof document !== "undefined") document.documentElement.lang = current;

export function getLocale(): Locale {
  return current;
}

/** Choix explicite du lecteur : appliqué partout et gardé dans le navigateur. */
export function setLocale(locale: Locale): void {
  saveLocale(locale);
  if (locale === current) return;
  current = locale;
  document.documentElement.lang = locale;
  listeners.forEach((listener) => listener(locale));
}

export function useLocale(): Locale {
  const [locale, setState] = useState(current);
  useEffect(() => {
    listeners.add(setState);
    setState(current);
    return () => void listeners.delete(setState);
  }, []);
  return locale;
}

export function useT<M extends MessageRecord>(messages: Messages<M>): Translate<M> {
  const locale = useLocale();
  return useMemo(() => translator(messages, locale), [messages, locale]);
}
